import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownToBracket } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Avatar } from '@components/basics/Images/Avatar';
import { AudioDownload } from '@components/icons/Rewards/audioDownload';
import { Comment10 } from '@components/icons/Rewards/comment10';
import { Reward10 } from '@components/icons/Rewards/reward10';
import { Reward20 } from '@components/icons/Rewards/reward20';
import { Reward30 } from '@components/icons/Rewards/reward30';
import { Reward40 } from '@components/icons/Rewards/reward40';
import { Reward50 } from '@components/icons/Rewards/reward50';
import { Button } from '@components/main-components/button/Button';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { RewardsModal } from '@components/modals/RewardsModal';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  AudioRewardCardFragmentDoc,
  getFragment,
  GoldenEggWinnerFragmentDoc,
  RewardSongInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useDownloadAudio } from '@hooks/useDownloadAudio';
import { useNftOwnershipData } from '@hooks/useNftOwnershipData';
import type { SongDefaultRewards } from '@utils/constants';
import {
  COLLECTORS_DOWNLOAD_AUDIO_COPY,
  COMMENT_REWARD_DESCRIPTION,
  REWARDS_TITLE,
  SONG_DEFAULT_REWARDS,
} from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { Fade } from '../shared';
import type { RewardType } from './shared';
import {
  Description,
  DESCRIPTION_CHAR_MAX,
  Ellipsis,
  getMaxHeight,
  GoldenEggWinner,
  Header,
  Overflow,
  RewardCardContainer,
  RewardImageContainer,
  Title,
  Wrapper,
} from './shared';

const AVATAR_SIZE = 16;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment RewardSongInfo on Reward {
    id
    title
    description
  }
`);

gql(/* GraphQL */ `
  fragment GoldenEggWinner on User {
    id
    avatar {
      id
      url
    }
    publicAddress
    username
    webappUri
  }
`);

export const defaultRewardsInfo = ({ isTieredRelease }: { isTieredRelease: boolean }) => {
  const rewards: {
    [key in SongDefaultRewards]: RewardType;
  } = {
    [SONG_DEFAULT_REWARDS.GOLDEN_EGG]: {
      icon: (
        <NextFutureImage
          alt="golden-egg-image"
          src="/images/golden_egg_icon.png"
          width={42}
          height={42}
        />
      ),
      title: REWARDS_TITLE[SONG_DEFAULT_REWARDS.GOLDEN_EGG],
      description: isTieredRelease
        ? 'Egg is randomly rewarded to one Limited Edition holder after the sale ends. Winner gets a unique 1/1 artwork chosen by the artist.'
        : 'The egg is only revealed after the song sells out. The winner receives a unique 1/1 NFT chosen by the artist.',
    },
    [SONG_DEFAULT_REWARDS.COMMENT]: {
      icon: <Comment10 />,
      title: REWARDS_TITLE[SONG_DEFAULT_REWARDS.COMMENT],
      description: COMMENT_REWARD_DESCRIPTION,
    },
  };

  return rewards;
};

export const getRewardIllustration = ({ index }: { index?: number }) => {
  if (index == undefined) {
    return <Reward50 />;
  }
  if (index % 5 === 1) {
    return <Reward10 />;
  } else if (index % 5 === 2) {
    return <Reward20 />;
  } else if (index % 5 === 3) {
    return <Reward30 />;
  } else if (index % 5 === 4) {
    return <Reward40 />;
  } else {
    return <Reward50 />;
  }
};

export const RewardCard = ({
  rewardFrag,
  rewardType,
  goldenEggWinnerFrag,
  index,
  isTieredRelease,
}: {
  rewardFrag?: FragmentType<RewardSongInfoFragmentDoc>;
  rewardType?: SongDefaultRewards;
  goldenEggWinnerFrag?: FragmentType<GoldenEggWinnerFragmentDoc>;
  index?: number;
  isTieredRelease: boolean;
}) => {
  const { openModal } = ModalContext.useContainer();
  const { tablet } = MediaQuery.useContainer();

  const reward = getFragment(RewardSongInfoFragmentDoc, rewardFrag);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const [hide, setHide] = useState<boolean>(false);
  const [isLongTitle, setIsLongTitle] = useState<boolean>(false);

  const rewardsInfo = defaultRewardsInfo({ isTieredRelease });

  const rewardTitle = rewardType ? rewardsInfo[rewardType].title : reward?.title;

  const rewardDescription = rewardType ? rewardsInfo[rewardType].description : reward?.description;

  const rewardIllustration = rewardType
    ? rewardsInfo[rewardType].icon
    : getRewardIllustration({ index });

  const maxHeightShortTitle = getMaxHeight({ titleType: 'short', tablet: !!tablet });
  const maxHeightLongTitle = getMaxHeight({ titleType: 'long', tablet: !!tablet });
  const maxChars = DESCRIPTION_CHAR_MAX;

  const isGoldenEgg = rewardType === SONG_DEFAULT_REWARDS.GOLDEN_EGG;
  const goldenEggWinner = getFragment(GoldenEggWinnerFragmentDoc, goldenEggWinnerFrag);
  const winnerHandle = goldenEggWinner && goldenEggWinner.username;

  React.useEffect(() => {
    const titleHeight = titleRef.current?.scrollHeight;
    const isOverflowingTitle = !!titleHeight && titleHeight > 18;
    setIsLongTitle(isOverflowingTitle);

    const isMaxChar = !!rewardDescription && rewardDescription?.length > maxChars;

    const maxHeight = isOverflowingTitle ? maxHeightLongTitle : maxHeightShortTitle;

    const isMaxHeight =
      !!containerRef.current?.scrollHeight && containerRef.current?.scrollHeight > maxHeight;

    setHide(isMaxChar || isMaxHeight);
  }, [
    containerRef,
    containerRef?.current?.scrollHeight,
    maxHeightShortTitle,
    maxHeightLongTitle,
    rewardDescription,
    maxChars,
  ]);

  return (
    <RewardCardContainer>
      <Wrapper>
        <Header>
          <RewardImageContainer>{rewardIllustration}</RewardImageContainer>
          <Title ref={titleRef}>{rewardTitle}</Title>
        </Header>
        <Description ref={containerRef} isLongTitle={isLongTitle}>
          <p>{rewardDescription}</p>
        </Description>

        {hide && (
          <Overflow>
            <Fade length={68}>
              <Ellipsis
                onClick={() => {
                  openModal(ModalType.SONG_REWARD, {
                    body: (
                      <RewardsModal
                        title={rewardTitle}
                        description={rewardDescription}
                        illustration={rewardIllustration}
                      />
                    ),
                  });
                }}
              >
                <p>...read more</p>
              </Ellipsis>
            </Fade>
          </Overflow>
        )}
      </Wrapper>

      {!!isGoldenEgg && (
        <GoldenEggWinner revealed={!!goldenEggWinner}>
          {!!goldenEggWinner ? (
            <>
              <h5>Winner: &nbsp;</h5>
              <AccessibleLink pathnameUri={webappUriPath(goldenEggWinner.webappUri)}>
                <UserContainer>
                  <AvatarContainer>
                    <Avatar
                      src={goldenEggWinner.avatar?.url}
                      walletAddress={goldenEggWinner.publicAddress}
                      size={AVATAR_SIZE}
                      borderRadius={4}
                      hasShadow
                      webappUri={goldenEggWinner.webappUri}
                    />
                  </AvatarContainer>

                  <p>{winnerHandle}</p>
                </UserContainer>
              </AccessibleLink>
            </>
          ) : (
            <p className="not-revealed">Not Revealed</p>
          )}
        </GoldenEggWinner>
      )}
    </RewardCardContainer>
  );
};

gql(/* GraphQL */ `
  fragment AudioRewardCard on Release {
    id
    track {
      id
    }
  }
`);

gql(/* GraphQL */ `
  query AudioRewardDownloadableURL($trackId: UUID!) {
    downloadableTrackURLForAudioReward(trackId: $trackId)
  }
`);

export const DraftAudioRewardCard = ({
  disabled,
  onDownload,
}: {
  disabled?: boolean;
  onDownload?: () => void;
}) => {
  return (
    <RewardCardContainer>
      <Wrapper>
        <Header>
          <RewardImageContainer>
            <AudioDownload />
          </RewardImageContainer>
          <Title>Audio Download</Title>
        </Header>
        <Description>
          <p>{COLLECTORS_DOWNLOAD_AUDIO_COPY}</p>
        </Description>
      </Wrapper>
      <AudioDownloadButtonWrapper
        size="XS"
        variant="text"
        label="Download"
        disabled={disabled}
        onClick={onDownload}
        leadingIcon={<FontAwesomeIcon icon={faArrowDownToBracket} fontSize={14} />}
      />
    </RewardCardContainer>
  );
};

const AudioDownloadButtonWrapper = styled(Button, {
  paddingX: '4px !important',
  marginX: -4,
});

export const AudioRewardCard = ({ data }: { data: FragmentType<AudioRewardCardFragmentDoc> }) => {
  const releaseData = getFragment(AudioRewardCardFragmentDoc, data);
  const { isReleaseOwner } = useNftOwnershipData({
    releaseId: releaseData.id,
  });

  const onDownload = useDownloadAudio(releaseData.track.id);

  return <DraftAudioRewardCard disabled={!isReleaseOwner} onDownload={onDownload} />;
};

const UserContainer = styled('div', {
  display: 'flex',
  gap: 4,
  color: '$neutral900',
  fontWeight: 500,
  alignItems: 'end',
});

const AvatarContainer = styled('div', {
  height: 18,
});
